<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ProFeatureNudge from './ProFeatureNudge.vue';
import { useInternalChatPro } from 'dashboard/composables/useInternalChatPro';

const store = useStore();
const { t } = useI18n();
const { maxPrivateChannels } = useInternalChatPro();

const isOpen = ref(false);
const step = ref(1);
const channelName = ref('');
const channelType = ref('public_channel');
const isCreating = ref(false);
const nameInputRef = ref(null);

const isPrivate = computed(() => channelType.value === 'private_channel');

const privateChannelCount = computed(() => {
  const channels = store.getters['internalChat/getChannels'] || [];
  return channels.filter(
    c => c.channel_type === 'private_channel' && c.status !== 'archived'
  ).length;
});

const canCreatePrivate = computed(() => {
  if (!maxPrivateChannels.value) return true;
  return privateChannelCount.value < maxPrivateChannels.value;
});

const privateLimitReached = computed(() => isPrivate.value && !canCreatePrivate.value);

const isStep1Valid = computed(() => channelName.value.trim().length > 0);

function open() {
  step.value = 1;
  channelName.value = '';
  channelType.value = 'public_channel';
  isOpen.value = true;
  setTimeout(() => nameInputRef.value?.focus(), 50);
}

function close() {
  if (isCreating.value) return;
  isOpen.value = false;
}

function goNext() {
  if (!isStep1Valid.value) return;
  step.value = 2;
}

function goBack() {
  step.value = 1;
  setTimeout(() => nameInputRef.value?.focus(), 50);
}

function handleNameKeydown(e) {
  if (e.key === 'Enter') goNext();
}

async function handleCreate() {
  if (privateLimitReached.value) return;
  isCreating.value = true;
  try {
    await store.dispatch('internalChat/create', {
      channel: {
        name: channelName.value.trim(),
        channel_type: channelType.value,
      },
    });
    useAlert(t('INTERNAL_CHAT.CHANNEL.CREATED'));
    isOpen.value = false;
  } catch (error) {
    if (error?.response?.status === 402) {
      channelType.value = 'public_channel';
    }
  } finally {
    isCreating.value = false;
  }
}

defineExpose({ open });
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-sm"
          @click="close"
        />

        <div
          class="relative z-10 w-full max-w-md rounded-xl bg-n-solid-2 shadow-xl"
        >
          <!-- Header -->
          <div class="flex items-start justify-between px-6 pt-6 pb-4 border-b border-n-slate-4">
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('INTERNAL_CHAT.CREATE.TITLE') }}
              </h2>
              <p
                v-if="step === 2 && channelName.trim()"
                class="mt-0.5 text-sm text-n-slate-10"
              >
                #{{ channelName.trim() }}
              </p>
            </div>
            <button
              class="flex-shrink-0 rounded-lg p-1.5 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="close"
            >
              <Icon icon="i-lucide-x" class="size-4" />
            </button>
          </div>

          <!-- Step 1: Name -->
          <div v-if="step === 1" class="px-6 py-5">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INTERNAL_CHAT.CREATE.NAME_LABEL') }}
              </label>
              <input
                ref="nameInputRef"
                v-model="channelName"
                type="text"
                class="w-full rounded-lg border border-n-slate-6 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-10 outline-none focus:border-n-brand transition-colors"
                :placeholder="t('INTERNAL_CHAT.CREATE.NAME_PLACEHOLDER')"
                @keydown="handleNameKeydown"
              />
              <p class="text-sm text-n-slate-10 leading-relaxed mt-1">
                {{ t('INTERNAL_CHAT.CREATE.NAME_HINT') }}
              </p>
            </div>
          </div>

          <!-- Step 2: Visibility -->
          <div v-else class="px-6 py-5">
            <div class="flex flex-col gap-2">
              <!-- Public -->
              <button
                type="button"
                class="flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors"
                :class="
                  !isPrivate
                    ? 'border-n-brand bg-n-blue-3'
                    : 'border-n-slate-6 hover:bg-n-alpha-1'
                "
                @click="channelType = 'public_channel'"
              >
                <div
                  class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-n-alpha-1"
                >
                  <i class="i-lucide-hash text-lg text-n-slate-11" />
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ t('INTERNAL_CHAT.CREATE.PUBLIC_TITLE') }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ t('INTERNAL_CHAT.CREATE.PUBLIC_DESC') }}
                  </p>
                </div>
                <span
                  class="flex size-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors"
                  :class="
                    !isPrivate ? 'border-n-brand bg-n-brand' : 'border-n-slate-6'
                  "
                >
                  <span v-if="!isPrivate" class="size-1.5 rounded-full bg-white" />
                </span>
              </button>

              <!-- Private -->
              <button
                type="button"
                class="flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors"
                :class="
                  isPrivate
                    ? 'border-n-brand bg-n-blue-3'
                    : 'border-n-slate-6 hover:bg-n-alpha-1'
                "
                @click="channelType = 'private_channel'"
              >
                <div
                  class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-n-alpha-1"
                >
                  <i class="i-lucide-lock text-base text-n-slate-11" />
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ t('INTERNAL_CHAT.CREATE.PRIVATE_TITLE') }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ t('INTERNAL_CHAT.CREATE.PRIVATE_DESC') }}
                  </p>
                </div>
                <span
                  class="flex size-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors"
                  :class="
                    isPrivate ? 'border-n-brand bg-n-brand' : 'border-n-slate-6'
                  "
                >
                  <span v-if="isPrivate" class="size-1.5 rounded-full bg-white" />
                </span>
              </button>

              <!-- Private hint -->
              <p
                v-if="isPrivate"
                class="flex items-center gap-1.5 px-1 text-xs text-n-slate-10"
              >
                <i class="i-lucide-info flex-shrink-0 text-sm" />
                {{ t('INTERNAL_CHAT.CREATE.PRIVATE_HINT') }}
              </p>

              <ProFeatureNudge
                v-if="privateLimitReached"
                feature="private_channels"
                inline
              />
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-2 border-t border-n-slate-4 px-6 py-4"
          >
            <template v-if="step === 1">
              <button
                class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
                :disabled="!isStep1Valid"
                @click="goNext"
              >
                {{ t('INTERNAL_CHAT.CREATE.NEXT') }}
              </button>
            </template>
            <template v-else>
              <button
                class="rounded-lg px-4 py-2 text-sm font-medium text-n-brand transition-colors hover:bg-n-blue-3 disabled:cursor-not-allowed disabled:opacity-40"
                :disabled="isCreating"
                @click="goBack"
              >
                {{ t('INTERNAL_CHAT.CREATE.BACK') }}
              </button>
              <button
                class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
                :disabled="isCreating || privateLimitReached"
                @click="handleCreate"
              >
                <span v-if="isCreating" class="flex items-center gap-2">
                  <i class="i-lucide-loader-circle animate-spin text-sm" />
                  {{ t('INTERNAL_CHAT.CREATE.CREATE') }}
                </span>
                <span v-else>{{ t('INTERNAL_CHAT.CREATE.CREATE') }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
